import { DiffDays, FormatDateShort } from "utils/dateUtils";
import { useMemo, useEffect, useState, useRef, } from "react";
import { useSelector } from "react-redux";
import { useAppDispatch } from "hooks/hooks";
import {
  selectState,
  setDeleteInputModalOpen,
  setCBOutputValue,
  setSelectedIndex,
  updateConcreteBaseInput,
  setUpdateInputItemPopover,
  // setNewPricePopover,
} from "../inputSlice";
import { toast } from "react-toastify";
import { FormatNumber, FormatNumberWithUnitOrNull } from "utils/numberUtils";
import { grey } from "@mui/material/colors";
import CancelIcon from "@mui/icons-material/Cancel";
import InfoTooltip from "components/Popup/InfoTooltip";
import { IConcreteBaseInput } from "Dashboard/scenes/ConcreteBase/domain/IConcreteBaseInput";
import { copyToClipboard } from "utils/clipboardUtils";
import { IConcreteBaseInputUpdate } from "Dashboard/scenes/ConcreteBase/domain/IConcreteBaseInputUpdate";
import { debounce } from "lodash";
import PartnerSimpleSelect from "Dashboard/scenes/Partners/components/PartnerSimpleSelect";
import { styles } from "Dashboard/components/SelectStyle";
import { IOption } from "Dashboard/domain/IOption";
import NumberInput from "Dashboard/components/NumberInput";
import { selectState as selectStateAuth } from "authentication/main.slice";
import { hasFullAccessTo, hasWriteAccessTo } from "utils/authUtil";
import { CONCRETE_BASE_MODULE_NAME } from "Dashboard/scenes/ConcreteBase/domain/ConcreteBaseModule";
import { debounceTimeout } from "utils/debounceUtil";
import { setOutputValue } from "Dashboard/scenes/Stock/secenes/Input/inputSlice";
import { useReduxField } from "hooks/useReduxField";
import { IStockInputUpdate } from "Dashboard/scenes/Stock/domain/IStockInputUpdate";

interface IProps {
  input: IConcreteBaseInput;
  partnerOptions: IOption[];
  index: number;
  lastTableElementRef?: any;
}

const ConcreteBaseInput = ({ input, partnerOptions, index, lastTableElementRef }: IProps) => {
  const { selectedIndex, outputValues } = useSelector(selectState);
  const { user } = useSelector(selectStateAuth);
  const dispatch = useAppDispatch();

  const valueForOutput = outputValues.map[input.inputId];
  const hasRemainingQuantity = Math.round(input.quantityAmountRemaining * 1000) > 0;
  const pricePerUnitNotSet
    = input.pricePerUnit === null
    || input.pricePerUnit === undefined
    || input.pricePerUnit === 0;
  const inputIsNotNew = -DiffDays(input.billOfLandingDate) > 14;
  const canSetValueForOutput =
    hasRemainingQuantity &&
    input.billOfLandingId?.toLowerCase() !== "obrisati" &&
    hasWriteAccessTo(user, CONCRETE_BASE_MODULE_NAME);

  const valueForOutputAsString = typeof valueForOutput === "number" ? valueForOutput.toString() : "";
  const [localQuantity, setLocalQuantity] = useState(valueForOutputAsString);
  useEffect(() => {
    setLocalQuantity(valueForOutputAsString);
  }, [valueForOutputAsString]);

  const commitQuantityDebounced = useMemo(
    () =>
      debounce((value: string) => {
        dispatch(
          setCBOutputValue({
            key: input.inputId,
            repository: input.repository,
            value: value && !Number.isNaN(Number(value)) ? Number(value) : undefined,
          })
        );
      }, 500),
    [dispatch, input]
  );

  const [warningMsg, setWarningMsg] = useState('');

  const onChangeQuantityForOutput = (value: string) => {
    if (value && !isValidQuantityForOutput()) return;
    setLocalQuantity(value);
    commitQuantityDebounced(value);
  };

  const isValidQuantityForOutput = () => {
    if (!outputValues.repository) return true;

    if (outputValues.repository.repositoryId !== input.repository.repositoryId) {
      setWarningMsg("Artikli moraju biti iz istog magacina!");
      return false;
    }

    if (input.repository.shortName === "SPbb" && Object.keys(outputValues.map).length > 0 && !outputValues.map.hasOwnProperty(input.inputId)) {
      setWarningMsg("Možete izabrati najviše jedan artikal iz magacina Sopstveni proizvodi bb!");
      return false;
    }
    return true;
  }

  useEffect(() => {
    if (warningMsg) {
      toast.warning(warningMsg);
      setWarningMsg('');
    }
  }, [warningMsg]);

  const onBlurQuantityForOutput = () => {
    if (!localQuantity) return;

    let currentValue = Number(localQuantity);
    const remainingValue = input.quantityAmountRemaining;

    if (currentValue > remainingValue) {
      const overflow = Math.round((currentValue - remainingValue) * 100) / 100;
      toast.info(
        `Nema ${currentValue} u magacinu. Upisujem ${remainingValue}, a nedostajućih ${overflow} upisujem u klipboard.`
      );
      copyToClipboard(overflow.toString());
      currentValue = remainingValue;
      setLocalQuantity(currentValue.toString());
    }

    commitQuantityDebounced(currentValue.toString());
  };

  const receiptNumberChangedRef = useRef(false);
  const updateBasicFieldWithDelay = useMemo(
    () => debounce(async (data: IConcreteBaseInputUpdate, showToast = true) => {
      try {
        await dispatch(updateConcreteBaseInput(data)).unwrap();
        if (showToast) {
          toast.success("Izmena je uspešno zabeležena.");
          receiptNumberChangedRef.current = false;
        }
      } catch (status: any) {
        if (status === 403 || status === 400) {
          toast.error("Neočekivana greška");
        } else {
          toast.error("Nepredviđena greška");
        }
      }
    }, debounceTimeout),
    [dispatch]
  );

  const [receiptNumber, setReceiptNumber] = useReduxField<string, IStockInputUpdate>(
    input.receiptNumber,
    (value) => ({
      inputId: input.inputId,
      receiptNumber: value,
    }),
    (data) => {
      const showToast = receiptNumberChangedRef.current;
      updateBasicFieldWithDelay(data, showToast);
    }
  );

  const onReceiptNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    receiptNumberChangedRef.current = true;
    setReceiptNumber(e.target.value);
  };

  const onChangePartner = (newPartnerId: number) => {
    updateBasicFieldWithDelay({ inputId: input.inputId, supplierPartnerId: newPartnerId });
  };

  return (
    <>
      <div
        className={`row ${index === selectedIndex ? " active" : ""}`}
        key={input.inputId}
        onClick={() => dispatch(setSelectedIndex(index))}
        ref={lastTableElementRef}
      >
        <div className="col">
          <input className={`bg-item bg-item-low-importance center ${!input.receiptNumber ? " bg-danger" : ""}`}
            type="text"
            value={receiptNumber}
            onChange={onReceiptNumberChange}
            disabled={!hasWriteAccessTo(user, CONCRETE_BASE_MODULE_NAME)}
          />
        </div>
        <div className="col">
          <input className="bg-item bg-item-low-importance center"
            type="text"
            defaultValue={input.billOfLandingId}
            onChange={(e) => updateBasicFieldWithDelay({ inputId: input.inputId, billOfLandingId: e.target.value })}
            disabled={!hasWriteAccessTo(user, CONCRETE_BASE_MODULE_NAME)}
          />
        </div>
        <div className="col strong">
          <div className={`bg-item overflowMinWidth center
                ${hasWriteAccessTo(user, CONCRETE_BASE_MODULE_NAME)
              ? "hoverBorder" : ""}`}
            onClick={hasWriteAccessTo(user, CONCRETE_BASE_MODULE_NAME)
              ? (e) => {
                dispatch(
                  setUpdateInputItemPopover({
                    isOpen: true,
                    left: e.clientX,
                    top: e.clientY,
                    itemRowIndex: index,
                    focus: "itemCode",
                  })
                )
              }
              : undefined}>
            <InfoTooltip classesProp="bg-text-overflow"
              tooltip={input.itemCode} element="span">
              {input.itemCode}
            </InfoTooltip>
          </div>
        </div>
        <div className="col center">
          <div className="bg-item bg-item-border-light danger">
            <InfoTooltip tooltip={input.repository.name} element="span">
              <span>{input.repository.shortName}</span>
            </InfoTooltip>
          </div>
        </div>
        <div className="col uppercase">
          <div className={`bg-item overflowMinWidth strong ${!hasRemainingQuantity ? "light" : ""}`}>
            <InfoTooltip classesProp="bg-text-overflow"
              tooltip={input.item.name} element="span">
              {input.item.name}
            </InfoTooltip>
          </div>
        </div>
        <div className="col center strong">
          <NumberInput
            formatAs="quantity"
            value={localQuantity}
            onChange={(_: string, unmaskedValue: string) => onChangeQuantityForOutput(unmaskedValue)}
            onBlur={onBlurQuantityForOutput}
            className={`bg-item-fix danger${canSetValueForOutput ? " hoverBorder" : ""}`}
            disabled={!canSetValueForOutput}
          />
        </div>
        <div className="col">
          <span className="bg-ch-fix">
            <input type="checkbox"
              readOnly
              checked={valueForOutput !== undefined
                && valueForOutput > 0
                && valueForOutput <= input.quantityAmountRemaining
              } />
          </span>
        </div>
        <div className="col">
          {hasWriteAccessTo(user, CONCRETE_BASE_MODULE_NAME)
            ? (
              <div className="bg-item bg-item-low-importance">
                <PartnerSimpleSelect
                  customStyles={styles.dropdownInTable}
                  defaultValue={input.supplier?.partnerId}
                  options={partnerOptions}
                  onChange={onChangePartner} />
              </div>
            )
            : (
              <InfoTooltip tooltip={input.supplier.fullName}
                classesProp="bg-item light overflowMinWidth">
                <span className="bg-text-overflow">
                  {input.supplier.fullName}
                </span>
              </InfoTooltip>
            )}
        </div>
        <div className="col">
          <div className="bg-item overflowMinWidth right">
            <InfoTooltip classesProp="bg-text-overflow"
              tooltip={`${FormatNumber(input.quantityAmountTotal)}`}>
              {FormatNumber(input.quantityAmountTotal)}
            </InfoTooltip>
          </div>
        </div>
        <div className="col">
          <div className="bg-item bg-transparent">
            <span>{input.quantityUnit.name}</span>
          </div>
        </div>
        <div className="col">
          <div className={`bg-item overflowMinWidth right
              ${hasWriteAccessTo(user, CONCRETE_BASE_MODULE_NAME)
              ? ' hoverBorder' : ''}
              ${pricePerUnitNotSet && inputIsNotNew ? ' bg-danger' : ''}`}
            onClick={hasWriteAccessTo(user, CONCRETE_BASE_MODULE_NAME)
              ? (e) => {
                dispatch(
                  setUpdateInputItemPopover({
                    isOpen: true,
                    left: e.clientX,
                    top: e.clientY,
                    itemRowIndex: index,
                    focus: "pricePerUnit",
                  })
                )
              }
              : undefined}>
            <InfoTooltip element="span" classesProp="bg-text-overflow"
              tooltip={FormatNumberWithUnitOrNull(input.pricePerUnit, "rsd") ?? undefined}
            >
              {FormatNumberWithUnitOrNull(input.pricePerUnit, "rsd")}
            </InfoTooltip>
          </div>
        </div>
        <div className="col">
          <div className="bg-item right">
            <NumberInput
              formatAs="rsd"
              defaultValue={`${input.deliveryPrice ?? ""}`}
              onChange={(_: string, __: string, parsedValue: number) => {
                if (parsedValue !== input.deliveryPrice) {
                  updateBasicFieldWithDelay({ inputId: input.inputId, deliveryPrice: parsedValue })
                }
              }}
              disabled={!hasWriteAccessTo(user, CONCRETE_BASE_MODULE_NAME)}
            />
          </div>
        </div>
        <div className="col">
          <div className="bg-item right">
            <span className="danger">
              {FormatNumber(input.quantityAmountRemaining)}
            </span>
          </div>
        </div>
        <div className="col">
          <div className="bg-item overflowMinWidth right">
            <InfoTooltip element="span" classesProp="bg-text-overflow"
              tooltip={FormatNumberWithUnitOrNull(input.totalPrice, 'rsd') ?? undefined}
            >
              {FormatNumberWithUnitOrNull(input.totalPrice, 'rsd')}
            </InfoTooltip>
          </div>
        </div>
        <div className="col light">
          <div className="bg-item center">
            <span>{FormatDateShort(input.billOfLandingDate)}</span>
          </div>
        </div>
        <div className="col center">
          {false && hasFullAccessTo(user, CONCRETE_BASE_MODULE_NAME)
            ? (
              <span
                onClick={(e) => {
                  e.stopPropagation();
                  dispatch(
                    setDeleteInputModalOpen({ visible: true, index: 2 })
                  );
                }}
              >
                <CancelIcon
                  style={{ cursor: "pointer", fontSize: 15, color: grey[300] }}
                />
              </span>
            )
            : (<></>)}
        </div>
        <div className="col light">
          <div className="bg-item bg-transparent">
            <span>{input.broughtBy?.name}</span>
          </div>
        </div>
        <div className="col">
          <input className="bg-item bg-transparent light" type="text"
            defaultValue={input.note}
            onChange={(e) => updateBasicFieldWithDelay({ inputId: input.inputId, note: e.target.value })}
            disabled={!hasWriteAccessTo(user, CONCRETE_BASE_MODULE_NAME)}
          />
        </div>
      </div>
    </>
  );
};

export default ConcreteBaseInput;